/**
 * B2BConsignmentReportPage — דיווח מכירות קונסגנציה
 *
 * Accessible to B2B clients/agents whose active client has allow_consignment=true.
 * The user chooses:
 *   1. report_mode: 'sold_list' (מה נמכר) | 'remaining_list' (מה נשאר)
 *   2. input_mode:  'form' (רשימה) | 'barcode' (ברקוד / מקט)
 *
 * Period defaults:
 *   • period_from: period_to of the last report for this client (or empty)
 *   • period_to:   today
 *
 * Submission calls submit_consignment_sale_report() RPC.
 * Report does NOT move inventory — admin approval (phase 3) triggers the invoice.
 */

import { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { PackageOpen, ClipboardCheck, ArrowRightLeft, Loader as Loader2, CircleAlert as AlertCircle, CircleCheck as CheckCircle2, Barcode, List, TrendingDown, TrendingUp, Search, Plus, Minus, Trash2, Send, CalendarDays, ChevronRight, ChevronLeft, Info } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { useB2BUser } from './B2BUserContext';

// ─── Types ────────────────────────────────────────────────────────────────────

type ReportMode  = 'sold_list' | 'remaining_list';
type InputMode   = 'form' | 'barcode';

interface StockProduct {
  product_id:   string;
  sku:          string | null;
  product_name: string;
  image_url:    string | null;
  unit_price:   number;
  stock_qty:    number;
}

interface ReportLine extends StockProduct {
  reported_qty: number;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function todayISO(): string { return new Date().toISOString().slice(0, 10); }

function formatDate(iso: string): string {
  return new Date(iso + 'T00:00:00').toLocaleDateString('he-IL');
}

function formatQty(n: number): string {
  if (Number.isInteger(n)) return String(n);
  return parseFloat(n.toFixed(3)).toString();
}

// ─── Step indicator ───────────────────────────────────────────────────────────

function StepDot({ n, active, done }: { n: number; active: boolean; done: boolean }) {
  return (
    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold
                     transition-all duration-200
                     ${done   ? 'bg-emerald-500 text-white' :
                       active ? 'bg-orange-500 text-white ring-4 ring-orange-100' :
                                'bg-gray-100 text-gray-400'}`}>
      {done ? <CheckCircle2 className="w-4 h-4" /> : n}
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export function B2BConsignmentReportPage() {
  const navigate   = useNavigate();
  const { session } = useB2BUser();

  const companyId = session?.activeClientRecord?.company_id ?? null;
  const clientId  = session?.activeClientId ?? null;

  // ── Step wizard (1-3) ────────────────────────────────────────────────────────
  const [step, setStep] = useState<1 | 2 | 3>(1);

  // ── Step 1: mode choices + period ───────────────────────────────────────────
  const [reportMode, setReportMode] = useState<ReportMode>('sold_list');
  const [inputMode,  setInputMode]  = useState<InputMode>('form');
  const [periodFrom, setPeriodFrom] = useState<string>('');
  const [periodTo,   setPeriodTo]   = useState<string>(todayISO());

  // ── Step 2: product lines ────────────────────────────────────────────────────
  const [stock,      setStock]      = useState<StockProduct[]>([]);
  const [lines,      setLines]      = useState<ReportLine[]>([]);
  const [loadingStock, setLoadingStock] = useState(false);
  const [stockError,   setStockError]   = useState<string | null>(null);
  const [search,       setSearch]       = useState('');

  // barcode input
  const [barcodeInput, setBarcodeInput] = useState('');
  const barcodeRef = useRef<HTMLInputElement>(null);

  // ── Step 3: notes + submit ───────────────────────────────────────────────────
  const [notes,      setNotes]      = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitted,  setSubmitted]  = useState(false);
  const [reportId,   setReportId]   = useState<string | null>(null);

  // ── Load previous period_to on mount ─────────────────────────────────────────
  useEffect(() => {
    if (!companyId || !clientId) return;
    supabase.rpc('get_last_consignment_report_period_to', {
      p_company_id: companyId,
      p_client_id:  clientId,
    }).then(({ data }) => {
      if (data) setPeriodFrom(data as string);
    });
  }, [companyId, clientId]);

  // ── Load stock when entering step 2 (form mode) ───────────────────────────────
  const loadStock = useCallback(async () => {
    if (!companyId || !clientId) return;
    setLoadingStock(true);
    setStockError(null);
    const { data, error } = await supabase.rpc('get_consignment_stock_for_client', {
      p_company_id: companyId,
      p_client_id:  clientId,
    });
    setLoadingStock(false);
    if (error) { setStockError(error.message); return; }
    const products = (data as StockProduct[]) ?? [];
    setStock(products);
    // pre-fill lines with qty=0
    setLines(products.map(p => ({ ...p, reported_qty: 0 })));
  }, [companyId, clientId]);

  useEffect(() => {
    if (step === 2 && inputMode === 'form') loadStock();
  }, [step, inputMode, loadStock]);

  // Remaining mode needs the full stock list so unscanned products count as fully sold.
  useEffect(() => {
    if (step !== 2 || inputMode !== 'barcode' || reportMode !== 'remaining_list') return;
    if (stock.length > 0 || !companyId || !clientId) return;
    setLoadingStock(true);
    supabase.rpc('get_consignment_stock_for_client', {
      p_company_id: companyId,
      p_client_id:  clientId,
    }).then(({ data, error }) => {
      setLoadingStock(false);
      if (error) { setStockError(error.message); return; }
      setStock((data as StockProduct[]) ?? []);
    });
  }, [step, inputMode, reportMode, stock.length, companyId, clientId]);

  // ── Barcode lookup ────────────────────────────────────────────────────────────
  const handleBarcodeSubmit = useCallback(() => {
    const q = barcodeInput.trim().toLowerCase();
    if (!q) return;
    setBarcodeInput('');
    // Load stock lazily if not yet loaded
    const doLookup = (stockList: StockProduct[]) => {
      const found = stockList.find(
        p => (p.sku ?? '').toLowerCase() === q || p.product_name.toLowerCase() === q,
      );
      if (!found) {
        setStockError(`לא נמצא מוצר עם מקט/שם: "${barcodeInput.trim()}"`);
        return;
      }
      setStockError(null);
      setLines(prev => {
        const existing = prev.find(l => l.product_id === found.product_id);
        if (existing) {
          return prev.map(l =>
            l.product_id === found.product_id
              ? { ...l, reported_qty: l.reported_qty + 1 }
              : l,
          );
        }
        return [...prev, { ...found, reported_qty: 1 }];
      });
    };

    if (stock.length > 0) {
      doLookup(stock);
    } else {
      // lazy fetch for barcode mode
      setLoadingStock(true);
      supabase.rpc('get_consignment_stock_for_client', {
        p_company_id: companyId!,
        p_client_id:  clientId!,
      }).then(({ data, error }) => {
        setLoadingStock(false);
        if (error) { setStockError(error.message); return; }
        const products = (data as StockProduct[]) ?? [];
        setStock(products);
        doLookup(products);
      });
    }
  }, [barcodeInput, stock, companyId, clientId]);

  // auto-focus barcode field when entering barcode mode step 2
  useEffect(() => {
    if (step === 2 && inputMode === 'barcode') {
      setTimeout(() => barcodeRef.current?.focus(), 100);
    }
  }, [step, inputMode]);

  // ── Qty helpers ───────────────────────────────────────────────────────────────
  const setQty = (productId: string, qty: number) => {
    setLines(prev => prev.map(l =>
      l.product_id === productId ? { ...l, reported_qty: Math.max(0, qty) } : l,
    ));
  };

  const removeLine = (productId: string) => {
    setLines(prev => prev.filter(l => l.product_id !== productId));
  };

  // ── Submit ────────────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    if (submitLines.length === 0) {
      setSubmitError('יש להזין לפחות מוצר אחד עם כמות.');
      return;
    }
    if (!periodTo) {
      setSubmitError('יש לבחור תאריך עד.');
      return;
    }
    setSubmitting(true);
    setSubmitError(null);

    const { data, error } = await supabase.rpc('submit_consignment_sale_report', {
      p_company_id:  companyId!,
      p_client_id:   clientId!,
      p_period_from: periodFrom || null,
      p_period_to:   periodTo,
      p_report_mode: reportMode,
      p_input_mode:  inputMode,
      p_notes:       notes,
      p_lines: submitLines.map(l => ({
        product_id:   l.product_id,
        sku:          l.sku ?? '',
        product_name: l.product_name,
        image_url:    l.image_url ?? null,
        reported_qty: l.reported_qty,
        unit_price:   l.unit_price,
      })),
    });

    setSubmitting(false);
    if (error) { setSubmitError(error.message); return; }
    setReportId(data as string);
    setSubmitted(true);
  };

  // ── Search filter (form mode) ─────────────────────────────────────────────────
  const filteredLines = lines.filter(l => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return l.product_name.toLowerCase().includes(q) || (l.sku ?? '').toLowerCase().includes(q);
  });

  const activeLines = lines.filter(l => l.reported_qty > 0);

  // In remaining mode every stocked product is sent; a missing or 0 qty means it all sold.
  const submitLines: ReportLine[] = reportMode === 'remaining_list'
    ? stock.map(p => ({
        ...p,
        reported_qty: lines.find(l => l.product_id === p.product_id)?.reported_qty ?? 0,
      }))
    : activeLines;

  // ── Guard: not a consignment client ──────────────────────────────────────────
  if (!session) {
    return (
      <div className="flex items-center justify-center h-64 text-gray-400 text-sm">
        <Loader2 className="w-5 h-5 animate-spin ml-2" />
        טוען…
      </div>
    );
  }

  // ── Success screen ────────────────────────────────────────────────────────────
  if (submitted) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center p-6" dir="rtl">
        <div className="max-w-sm w-full text-center space-y-6">
          <div className="w-20 h-20 bg-emerald-50 rounded-full flex items-center justify-center mx-auto">
            <CheckCircle2 className="w-10 h-10 text-emerald-500" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-900 mb-2">הדיווח נשלח בהצלחה!</h2>
            <p className="text-gray-500 text-sm">
              דיווח המכירות שלך התקבל ויעובד על ידי הספק בהקדם.
              {reportId && (
                <span className="block mt-1 font-mono text-xs text-gray-400">
                  מזהה: {reportId.slice(0, 8)}…
                </span>
              )}
            </p>
          </div>
          <div className="bg-gray-50 rounded-xl p-4 text-sm text-gray-600 space-y-1 text-right">
            <div className="flex justify-between">
              <span className="text-gray-400">תקופה:</span>
              <span>{periodFrom ? formatDate(periodFrom) : 'פתוח'} — {formatDate(periodTo)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-400">מוצרים:</span>
              <span>{submitLines.length} פריטים</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-400">סוג דיווח:</span>
              <span>{reportMode === 'sold_list' ? 'מה נמכר' : 'מה נשאר'}</span>
            </div>
          </div>
          <button
            onClick={() => navigate('/b2b/dashboard')}
            className="w-full py-3 px-6 bg-orange-500 hover:bg-orange-600 text-white
                       font-medium rounded-xl transition-colors"
          >
            חזרה לדשבורד
          </button>
        </div>
      </div>
    );
  }

  // ─── Render wizard ────────────────────────────────────────────────────────────
  return (
    <div className="max-w-2xl mx-auto p-4 pb-12 space-y-6" dir="rtl">

      {/* Header */}
      <div className="flex items-center gap-3 pt-2">
        <div className="p-2 bg-orange-100 rounded-xl">
          <ClipboardCheck className="w-5 h-5 text-orange-600" />
        </div>
        <div>
          <h1 className="text-lg font-bold text-gray-900">דיווח מכירות קונסגנציה</h1>
          <p className="text-xs text-gray-500">
            {session.activeClientRecord?.name ?? ''}
          </p>
        </div>
      </div>

      {/* Step indicator */}
      <div className="flex items-center gap-2">
        {([1, 2, 3] as const).map((n, i) => (
          <div key={n} className="flex items-center gap-2">
            <StepDot n={n} active={step === n} done={step > n} />
            {i < 2 && (
              <div className={`flex-1 h-0.5 w-8 rounded
                ${step > n ? 'bg-emerald-400' : 'bg-gray-200'}`} />
            )}
          </div>
        ))}
        <span className="mr-2 text-xs text-gray-400">
          {step === 1 ? 'הגדרות' : step === 2 ? 'פרטי מכירה' : 'אישור ושליחה'}
        </span>
      </div>

      {/* ══════════════════ STEP 1: Settings ══════════════════ */}
      {step === 1 && (
        <div className="space-y-5">

          {/* Period */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 space-y-4">
            <h3 className="font-semibold text-gray-800 flex items-center gap-2">
              <CalendarDays className="w-4 h-4 text-orange-500" />
              תקופת המכירה המדווחת
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs text-gray-500 mb-1 block">מתאריך</label>
                <input type="date" value={periodFrom}
                  onChange={e => setPeriodFrom(e.target.value)}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm
                             focus:outline-none focus:ring-2 focus:ring-orange-400" />
                {!periodFrom && (
                  <p className="text-xs text-gray-400 mt-1">פתוח (לא הוגדר)</p>
                )}
              </div>
              <div>
                <label className="text-xs text-gray-500 mb-1 block">עד תאריך</label>
                <input type="date" value={periodTo}
                  onChange={e => setPeriodTo(e.target.value)}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm
                             focus:outline-none focus:ring-2 focus:ring-orange-400" />
              </div>
            </div>
          </div>

          {/* Report mode */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 space-y-3">
            <h3 className="font-semibold text-gray-800 flex items-center gap-2">
              <ArrowRightLeft className="w-4 h-4 text-orange-500" />
              מה אתה מדווח?
            </h3>
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={() => setReportMode('sold_list')}
                className={`flex flex-col items-center gap-2 p-4 rounded-xl border-2 transition-all
                  ${reportMode === 'sold_list'
                    ? 'border-orange-400 bg-orange-50'
                    : 'border-gray-100 bg-gray-50 hover:border-gray-200'}`}
              >
                <TrendingDown className={`w-6 h-6 ${reportMode === 'sold_list' ? 'text-orange-500' : 'text-gray-400'}`} />
                <span className={`text-sm font-medium ${reportMode === 'sold_list' ? 'text-orange-700' : 'text-gray-600'}`}>
                  מה נמכר
                </span>
                <span className="text-xs text-gray-400 text-center">
                  אדווח כמה יחידות מכל מוצר נמכרו
                </span>
              </button>
              <button
                onClick={() => setReportMode('remaining_list')}
                className={`flex flex-col items-center gap-2 p-4 rounded-xl border-2 transition-all
                  ${reportMode === 'remaining_list'
                    ? 'border-orange-400 bg-orange-50'
                    : 'border-gray-100 bg-gray-50 hover:border-gray-200'}`}
              >
                <TrendingUp className={`w-6 h-6 ${reportMode === 'remaining_list' ? 'text-orange-500' : 'text-gray-400'}`} />
                <span className={`text-sm font-medium ${reportMode === 'remaining_list' ? 'text-orange-700' : 'text-gray-600'}`}>
                  מה נשאר
                </span>
                <span className="text-xs text-gray-400 text-center">
                  אדווח כמה יחידות נותרו במלאי שלי
                </span>
              </button>
            </div>
            {reportMode === 'remaining_list' && (
              <div className="flex items-start gap-2 p-3 bg-blue-50 rounded-lg text-xs text-blue-700">
                <Info className="w-4 h-4 shrink-0 mt-0.5" />
                <span>המערכת תחשב אוטומטית מה נמכר: <strong>מה שנשלח פחות מה שנשאר = מה שנמכר</strong></span>
              </div>
            )}
          </div>

          {/* Input mode */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 space-y-3">
            <h3 className="font-semibold text-gray-800 flex items-center gap-2">
              <List className="w-4 h-4 text-orange-500" />
              איך תזין את הנתונים?
            </h3>
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={() => setInputMode('form')}
                className={`flex flex-col items-center gap-2 p-4 rounded-xl border-2 transition-all
                  ${inputMode === 'form'
                    ? 'border-orange-400 bg-orange-50'
                    : 'border-gray-100 bg-gray-50 hover:border-gray-200'}`}
              >
                <List className={`w-6 h-6 ${inputMode === 'form' ? 'text-orange-500' : 'text-gray-400'}`} />
                <span className={`text-sm font-medium ${inputMode === 'form' ? 'text-orange-700' : 'text-gray-600'}`}>
                  רשימה
                </span>
                <span className="text-xs text-gray-400 text-center">
                  בחירה מרשימת המוצרים הקיימים
                </span>
              </button>
              <button
                onClick={() => setInputMode('barcode')}
                className={`flex flex-col items-center gap-2 p-4 rounded-xl border-2 transition-all
                  ${inputMode === 'barcode'
                    ? 'border-orange-400 bg-orange-50'
                    : 'border-gray-100 bg-gray-50 hover:border-gray-200'}`}
              >
                <Barcode className={`w-6 h-6 ${inputMode === 'barcode' ? 'text-orange-500' : 'text-gray-400'}`} />
                <span className={`text-sm font-medium ${inputMode === 'barcode' ? 'text-orange-700' : 'text-gray-600'}`}>
                  ברקוד / מקט
                </span>
                <span className="text-xs text-gray-400 text-center">
                  סריקה או הקלדת מקטים
                </span>
              </button>
            </div>
          </div>

          <button
            onClick={() => setStep(2)}
            disabled={!periodTo}
            className="w-full flex items-center justify-center gap-2 py-3 px-6
                       bg-orange-500 hover:bg-orange-600 disabled:opacity-50
                       text-white font-medium rounded-xl transition-colors"
          >
            המשך
            <ChevronLeft className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* ══════════════════ STEP 2: Lines ══════════════════ */}
      {step === 2 && (
        <div className="space-y-4">

          {/* Period summary pill */}
          <div className="flex items-center gap-2 text-xs text-gray-500 bg-gray-50
                          rounded-lg px-3 py-2">
            <CalendarDays className="w-3.5 h-3.5 text-orange-400" />
            <span>
              תקופה: {periodFrom ? formatDate(periodFrom) : 'פתוח'} — {formatDate(periodTo)}
            </span>
            <span className="mx-1 text-gray-300">|</span>
            <span>{reportMode === 'sold_list' ? 'מה נמכר' : 'מה נשאר'}</span>
          </div>

          {/* ── BARCODE INPUT MODE ── */}
          {inputMode === 'barcode' && (
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 space-y-4">
              <h3 className="font-semibold text-gray-800 flex items-center gap-2">
                <Barcode className="w-4 h-4 text-orange-500" />
                סרוק או הקלד מקט
              </h3>
              <div className="flex gap-2">
                <input
                  ref={barcodeRef}
                  type="text"
                  value={barcodeInput}
                  onChange={e => setBarcodeInput(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') handleBarcodeSubmit(); }}
                  placeholder="הקלד מקט ולחץ Enter…"
                  className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-sm
                             focus:outline-none focus:ring-2 focus:ring-orange-400"
                  dir="ltr"
                />
                <button onClick={handleBarcodeSubmit}
                  className="px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white
                             rounded-lg text-sm font-medium transition-colors">
                  <Plus className="w-4 h-4" />
                </button>
              </div>
              {stockError && (
                <div className="flex items-center gap-2 text-xs text-red-600 bg-red-50
                                rounded-lg px-3 py-2">
                  <AlertCircle className="w-3.5 h-3.5 shrink-0" />
                  {stockError}
                </div>
              )}
              {loadingStock && (
                <div className="flex items-center gap-2 text-xs text-gray-400">
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  מחפש מוצר…
                </div>
              )}
            </div>
          )}

          {/* ── FORM INPUT MODE — search + list ── */}
          {inputMode === 'form' && (
            <>
              {loadingStock && (
                <div className="flex items-center justify-center py-10 gap-2 text-gray-400 text-sm">
                  <Loader2 className="w-5 h-5 animate-spin" />
                  טוען מלאי קונסגנציה…
                </div>
              )}
              {stockError && (
                <div className="flex items-center gap-3 p-4 bg-red-50 border border-red-200
                                rounded-xl text-sm text-red-700">
                  <AlertCircle className="w-4 h-4 shrink-0" />
                  {stockError}
                </div>
              )}
              {!loadingStock && !stockError && (
                <>
                  <div className="relative">
                    <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4
                                       text-gray-400 pointer-events-none" />
                    <input type="text" value={search} onChange={e => setSearch(e.target.value)}
                      placeholder="חיפוש לפי שם מוצר או מקט…"
                      className="w-full pr-9 pl-4 py-2.5 border border-gray-200 rounded-xl text-sm
                                 focus:outline-none focus:ring-2 focus:ring-orange-400" />
                  </div>

                  {filteredLines.length === 0 && (
                    <div className="text-center py-10 text-gray-400 text-sm">
                      <PackageOpen className="w-8 h-8 mx-auto mb-2 opacity-40" />
                      {lines.length === 0 ? 'לא נמצאו מוצרים במלאי הקונסגנציה' : 'אין תוצאות לחיפוש'}
                    </div>
                  )}

                  <div className="space-y-2">
                    {filteredLines.map(line => (
                      <div key={line.product_id}
                        className="bg-white rounded-xl border border-gray-100 shadow-sm
                                   flex items-center gap-3 p-3">
                        {/* Image */}
                        {line.image_url ? (
                          <img src={line.image_url} alt={line.product_name}
                            className="w-10 h-10 rounded-lg object-cover shrink-0 border border-gray-100" />
                        ) : (
                          <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center
                                          justify-center shrink-0">
                            <PackageOpen className="w-5 h-5 text-gray-300" />
                          </div>
                        )}

                        {/* Info */}
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-gray-800 truncate">
                            {line.product_name}
                          </div>
                          <div className="flex items-center gap-2 mt-0.5">
                            {line.sku && (
                              <span className="font-mono text-xs text-gray-400">{line.sku}</span>
                            )}
                            <span className="text-xs text-gray-400">
                              במלאי: <strong>{formatQty(line.stock_qty)}</strong>
                            </span>
                          </div>
                        </div>

                        {/* Qty control */}
                        <div className="flex items-center gap-1 shrink-0">
                          <button
                            onClick={() => setQty(line.product_id, line.reported_qty - 1)}
                            className="w-7 h-7 rounded-lg bg-gray-100 hover:bg-gray-200
                                       flex items-center justify-center transition-colors">
                            <Minus className="w-3.5 h-3.5 text-gray-600" />
                          </button>
                          <input
                            type="number"
                            value={line.reported_qty || ''}
                            onChange={e => setQty(line.product_id, Number(e.target.value))}
                            min="0"
                            className="w-14 text-center border border-gray-200 rounded-lg py-1
                                       text-sm font-semibold focus:outline-none focus:ring-2
                                       focus:ring-orange-400"
                            dir="ltr"
                          />
                          <button
                            onClick={() => setQty(line.product_id, line.reported_qty + 1)}
                            className="w-7 h-7 rounded-lg bg-orange-100 hover:bg-orange-200
                                       flex items-center justify-center transition-colors">
                            <Plus className="w-3.5 h-3.5 text-orange-600" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </>
          )}

          {/* Lines added (barcode mode shows a running list) */}
          {inputMode === 'barcode' && lines.length > 0 && (
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
              <div className="px-4 py-3 border-b border-gray-50 text-sm font-semibold text-gray-700">
                מוצרים שנוספו ({lines.length})
              </div>
              <div className="divide-y divide-gray-50">
                {lines.map(line => (
                  <div key={line.product_id} className="flex items-center gap-3 px-4 py-3">
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-gray-800 truncate">
                        {line.product_name}
                      </div>
                      {line.sku && (
                        <div className="font-mono text-xs text-gray-400">{line.sku}</div>
                      )}
                    </div>
                    <div className="flex items-center gap-1">
                      <button onClick={() => setQty(line.product_id, line.reported_qty - 1)}
                        className="w-7 h-7 rounded-lg bg-gray-100 hover:bg-gray-200
                                   flex items-center justify-center transition-colors">
                        <Minus className="w-3.5 h-3.5" />
                      </button>
                      <input type="number" value={line.reported_qty}
                        onChange={e => setQty(line.product_id, Number(e.target.value))}
                        className="w-14 text-center border border-gray-200 rounded-lg py-1
                                   text-sm font-semibold focus:outline-none"
                        dir="ltr" />
                      <button onClick={() => setQty(line.product_id, line.reported_qty + 1)}
                        className="w-7 h-7 rounded-lg bg-orange-100 hover:bg-orange-200
                                   flex items-center justify-center transition-colors">
                        <Plus className="w-3.5 h-3.5 text-orange-600" />
                      </button>
                      <button onClick={() => removeLine(line.product_id)}
                        className="w-7 h-7 rounded-lg bg-red-50 hover:bg-red-100
                                   flex items-center justify-center transition-colors mr-1">
                        <Trash2 className="w-3.5 h-3.5 text-red-500" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Summary bar */}
          {activeLines.length > 0 && (
            <div className="flex items-center gap-2 px-4 py-2.5 bg-emerald-50 border
                            border-emerald-100 rounded-xl text-sm text-emerald-700">
              <CheckCircle2 className="w-4 h-4 shrink-0" />
              <span>{activeLines.length} מוצרים עם כמות — סה"כ{' '}
                {activeLines.reduce((s, l) => s + l.reported_qty, 0)} יח'
              </span>
            </div>
          )}

          {/* Navigation */}
          <div className="flex gap-3">
            <button onClick={() => setStep(1)}
              className="flex items-center gap-2 px-4 py-3 border border-gray-200
                         rounded-xl text-sm text-gray-600 hover:bg-gray-50 transition-colors">
              <ChevronRight className="w-4 h-4" />
              חזרה
            </button>
            <button
              onClick={() => setStep(3)}
              disabled={submitLines.length === 0 || loadingStock}
              className="flex-1 flex items-center justify-center gap-2 py-3 px-6
                         bg-orange-500 hover:bg-orange-600 disabled:opacity-50
                         text-white font-medium rounded-xl transition-colors"
            >
              המשך לאישור
              <ChevronLeft className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* ══════════════════ STEP 3: Review + Submit ══════════════════ */}
      {step === 3 && (
        <div className="space-y-4">

          {/* Summary */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 space-y-4">
            <h3 className="font-semibold text-gray-800">סיכום הדיווח</h3>

            <div className="grid grid-cols-2 gap-3 text-sm">
              <div className="bg-gray-50 rounded-xl p-3">
                <div className="text-xs text-gray-400 mb-1">תקופה</div>
                <div className="font-medium text-gray-800">
                  {periodFrom ? formatDate(periodFrom) : 'פתוח'} — {formatDate(periodTo)}
                </div>
              </div>
              <div className="bg-gray-50 rounded-xl p-3">
                <div className="text-xs text-gray-400 mb-1">סוג דיווח</div>
                <div className="font-medium text-gray-800">
                  {reportMode === 'sold_list' ? 'מה נמכר' : 'מה נשאר'}
                </div>
              </div>
              <div className="bg-gray-50 rounded-xl p-3">
                <div className="text-xs text-gray-400 mb-1">שיטת הזנה</div>
                <div className="font-medium text-gray-800">
                  {inputMode === 'form' ? 'רשימה' : 'ברקוד / מקט'}
                </div>
              </div>
              <div className="bg-gray-50 rounded-xl p-3">
                <div className="text-xs text-gray-400 mb-1">סה"כ פריטים</div>
                <div className="font-medium text-gray-800">
                  {submitLines.length} מוצרים,{' '}
                  {submitLines.reduce((s, l) => s + l.reported_qty, 0)} יח'
                  {reportMode === 'remaining_list' && ' נשארו'}
                </div>
              </div>
            </div>

            {/* Lines preview */}
            <div className="border border-gray-100 rounded-xl overflow-hidden">
              <div className="bg-gray-50 px-4 py-2 text-xs font-medium text-gray-500 border-b border-gray-100">
                פירוט שורות
              </div>
              <div className="divide-y divide-gray-50 max-h-56 overflow-y-auto">
                {submitLines.map(line => (
                  <div key={line.product_id}
                    className="flex items-center justify-between px-4 py-2.5 text-sm">
                    <div className="min-w-0">
                      <span className="font-medium text-gray-800">{line.product_name}</span>
                      {line.sku && (
                        <span className="mr-2 font-mono text-xs text-gray-400">{line.sku}</span>
                      )}
                    </div>
                    <span className={`font-bold shrink-0 mr-2
                      ${reportMode === 'sold_list' ? 'text-red-600' : 'text-emerald-600'}`}>
                      {formatQty(line.reported_qty)} יח'
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Notes */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
            <label className="text-sm font-medium text-gray-700 mb-2 block">
              הערות (אופציונלי)
            </label>
            <textarea
              value={notes}
              onChange={e => setNotes(e.target.value)}
              rows={3}
              placeholder="הוסף הערה לספק אם יש צורך…"
              className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm
                         focus:outline-none focus:ring-2 focus:ring-orange-400 resize-none"
            />
          </div>

          {submitError && (
            <div className="flex items-center gap-3 p-4 bg-red-50 border border-red-200
                            rounded-xl text-sm text-red-700">
              <AlertCircle className="w-4 h-4 shrink-0" />
              {submitError}
            </div>
          )}

          {/* Navigation */}
          <div className="flex gap-3">
            <button onClick={() => setStep(2)}
              className="flex items-center gap-2 px-4 py-3 border border-gray-200
                         rounded-xl text-sm text-gray-600 hover:bg-gray-50 transition-colors">
              <ChevronRight className="w-4 h-4" />
              חזרה
            </button>
            <button
              onClick={handleSubmit}
              disabled={submitting}
              className="flex-1 flex items-center justify-center gap-2 py-3 px-6
                         bg-orange-500 hover:bg-orange-600 disabled:opacity-50
                         text-white font-medium rounded-xl transition-colors"
            >
              {submitting ? (
                <><Loader2 className="w-4 h-4 animate-spin" />שולח…</>
              ) : (
                <><Send className="w-4 h-4" />שלח דיווח</>
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
